import { useState } from "react";
import { LEGAL_DOCS } from "../lib/legalDocs";

interface AuthFormProps {
  onSignIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  onSignUp: (email: string, password: string, name: string) => Promise<{ error: Error | null }>;
  onResetPassword: (email: string) => Promise<{ error: Error | null }>;
  onUpdatePassword?: (password: string) => Promise<{ error: Error | null }>;
  initialMode?: "login" | "register" | "forgot" | "new-password";
  onClose?: () => void;
}

export function AuthForm({ onSignIn, onSignUp, onResetPassword, onUpdatePassword, initialMode = "login", onClose }: AuthFormProps) {
  const [mode, setMode] = useState<"login" | "register" | "forgot" | "new-password">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  const switchMode = (newMode: "login" | "register" | "forgot" | "new-password") => {
    setMode(newMode);
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    if (mode === "register") {
      if (!consent) {
        setError("Musíte súhlasiť s obchodnými podmienkami a pravidlami štúdia.");
        setLoading(false);
        return;
      }
      const { error: err } = await onSignUp(email, password, name);
      if (err) {
        setError(err.message);
      } else {
        setSuccess("Účet vytvorený! Skontrolujte email pre potvrdenie.");
      }
    } else if (mode === "forgot") {
      if (!email) {
        setError("Zadajte emailovú adresu.");
        setLoading(false);
        return;
      }
      const { error: err } = await onResetPassword(email);
      if (err) {
        setError(err.message);
      } else {
        setSuccess("Odkaz na obnovenie hesla bol odoslaný na váš email.");
      }
    } else if (mode === "new-password") {
      if (password.length < 6) {
        setError("Heslo musí mať minimálne 6 znakov.");
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError("Heslá sa nezhodujú.");
        setLoading(false);
        return;
      }
      if (!onUpdatePassword) {
        setError("Aktualizácia hesla nie je dostupná.");
        setLoading(false);
        return;
      }
      const { error: err } = await onUpdatePassword(password);
      if (err) {
        setError(err.message);
      } else {
        setSuccess("Heslo bolo zmenené! Môžete sa prihlásiť.");
        setTimeout(() => switchMode("login"), 2000);
      }
    } else {
      const { error: err } = await onSignIn(email, password);
      if (err) {
        setError(err.message);
      }
    }
    setLoading(false);
  };

  const title = {
    login: "Prihlásiť sa",
    register: "Vytvoriť účet",
    forgot: "Obnoviť heslo",
    "new-password": "Nové heslo",
  }[mode];

  const submitLabel = {
    login: "Prihlásiť sa",
    register: "Registrovať sa",
    forgot: "Obnoviť heslo",
    "new-password": "Uložiť nové heslo",
  }[mode];

  return (
    <div className="auth-form">
      <div className="auth-form-header">
        <h2>{title}</h2>
        {onClose && (
          <button className="btn-close" onClick={onClose} aria-label="Zavrieť">×</button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {mode === "register" && (
          <div className="form-field">
            <label>Meno</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Vaše meno"
            />
          </div>
        )}

        {(mode === "login" || mode === "register" || mode === "forgot") && (
          <div className="form-field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="vas@email.sk"
            />
          </div>
        )}

        {(mode === "login" || mode === "register") && (
          <div className="form-field">
            <label>Heslo</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Minimálne 6 znakov"
            />
          </div>
        )}

        {mode === "new-password" && (
          <>
            <div className="form-field">
              <label>Nové heslo</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Minimálne 6 znakov"
              />
            </div>
            <div className="form-field">
              <label>Potvrdiť heslo</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Zopakujte heslo"
              />
            </div>
          </>
        )}

        {mode === "register" && (
          <div className="form-field checkbox">
            <label>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                Súhlasím so{" "}
                <a href={LEGAL_DOCS.terms} target="_blank" rel="noopener noreferrer">Všeobecnými obchodnými podmienkami</a>{" "}
                a <a href={LEGAL_DOCS.studioRules} target="_blank" rel="noopener noreferrer">Pravidlami štúdia</a>.
                Beriem na vedomie{" "}
                <a href={LEGAL_DOCS.privacy} target="_blank" rel="noopener noreferrer">Zásady ochrany osobných údajov</a>.
              </span>
            </label>
          </div>
        )}

        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">{success}</div>}

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? "Načítavam..." : submitLabel}
        </button>
      </form>

      {mode === "login" && (
        <div className="auth-forgot">
          <button className="link-btn" onClick={() => switchMode("forgot")}>
            Zabudli ste heslo?
          </button>
        </div>
      )}

      <div className="auth-switch">
        {mode === "login" && (
          <p>
            Nemáte účet?{" "}
            <button className="link-btn" onClick={() => switchMode("register")}>
              Registrujte sa
            </button>
          </p>
        )}
        {mode === "register" && (
          <p>
            Už máte účet?{" "}
            <button className="link-btn" onClick={() => switchMode("login")}>
              Prihláste sa
            </button>
          </p>
        )}
        {mode === "forgot" && (
          <p>
            <button className="link-btn" onClick={() => switchMode("login")}>
              ← Späť na prihlásenie
            </button>
          </p>
        )}
        {mode === "new-password" && success && (
          <p>
            <button className="link-btn" onClick={() => switchMode("login")}>
              Prihlásiť sa
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
